import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  TrendingUp, TrendingDown, BarChart3, PieChart as PieIcon, DollarSign,
  Layers, Package, Users, AlertCircle, ShoppingCart, Activity,
  FileSpreadsheet, Truck, Sparkles,
} from 'lucide-react'
import { VisaoDonoPage } from '@/pages/DashboardsPage'
import ResumoFinanceiroMLPage from '@/pages/ResumoFinanceiroML'
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts'
import { api, formatMoney } from '@/lib/api'

const PALETA = [
  '#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444',
  '#ec4899', '#06b6d4', '#84cc16', '#a855f7', '#0ea5e9',
]
const CORES_CANAL: Record<string, string> = {
  mercado_livre: '#facc15', tray: '#ec4899', loja: '#3b82f6',
  balcao: '#a78bfa', digitado: '#94a3b8', outro: '#64748b',
  com_adiantamento: '#f97316', sem_adiantamento: '#10b981',
  tray_provavel: '#ec4899',
}
const MESES_ABREV = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ']

type Aba = 'visao' | 'dono' | 'resumo' | 'dre' | 'vendas' | 'evolucao' | 'produtos' | 'marketplace' | 'despesas' | 'fluxo'

export default function ControladoriaPage() {
  const [aba, setAba] = useState<Aba>('visao')
  const [periodo, setPeriodo] = useState<{ ano: number; mes: number }>(() => {
    const d = new Date()
    return { ano: d.getFullYear(), mes: d.getMonth() + 1 }
  })

  return (
    <div className="p-4 lg:p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 size={22} className="text-violet-600" /> Controladoria
          </h1>
          <p className="text-xs text-ink-500">Análise gerencial completa — DRE, margem, marketplace, despesas</p>
        </div>
        {aba !== 'dre' && <SeletorPeriodo periodo={periodo} setPeriodo={setPeriodo} />}
      </div>

      <div className="flex gap-1 border-b border-ink-200 overflow-x-auto whitespace-nowrap">
        {[
          { k: 'visao', label: 'Visão Executiva', ico: <Activity size={13} /> },
          { k: 'dono', label: 'Visão do Dono', ico: <Sparkles size={13} /> },
          { k: 'resumo', label: 'Resumo Financeiro', ico: <DollarSign size={13} /> },
          { k: 'dre', label: 'DRE Mensal', ico: <FileSpreadsheet size={13} /> },
          { k: 'vendas', label: 'Vendas Detalhada', ico: <ShoppingCart size={13} /> },
          { k: 'evolucao', label: 'Evolução', ico: <TrendingUp size={13} /> },
          { k: 'produtos', label: 'Margem Produtos', ico: <Package size={13} /> },
          { k: 'marketplace', label: 'Marketplace', ico: <Truck size={13} /> },
          { k: 'despesas', label: 'Despesas', ico: <PieIcon size={13} /> },
          { k: 'fluxo', label: 'Fluxo & Aging', ico: <Users size={13} /> },
        ].map(({ k, label, ico }) => (
          <button key={k} onClick={() => setAba(k as Aba)}
            className={`px-3 py-2 text-xs font-medium flex items-center gap-1.5 border-b-2 -mb-px transition ${
              aba === k
                ? 'border-violet-600 text-violet-700'
                : 'border-transparent text-ink-600 hover:text-ink-900'
            }`}>
            {ico} {label}
          </button>
        ))}
      </div>

      {aba === 'visao' && <VisaoExecutiva periodo={periodo} />}
      {aba === 'dono' && <VisaoDonoPage />}
      {aba === 'resumo' && <ResumoFinanceiro periodo={periodo} />}
      {aba === 'dre' && <DREMensal />}
      {aba === 'vendas' && <VendasDetalhada periodo={periodo} />}
      {aba === 'evolucao' && <Evolucao />}
      {aba === 'produtos' && <MargemProdutos periodo={periodo} />}
      {aba === 'marketplace' && <Marketplace />}
      {aba === 'despesas' && <Despesas periodo={periodo} />}
      {aba === 'fluxo' && <Fluxo />}
    </div>
  )
}

function SeletorPeriodo({ periodo, setPeriodo }: any) {
  const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']
  return (
    <div className="flex gap-1 items-center bg-white border border-ink-200 rounded p-1">
      <select value={periodo.mes} onChange={e => setPeriodo({ ...periodo, mes: Number(e.target.value) })}
        className="text-xs border-0 outline-none px-2 py-1 bg-transparent">
        {meses.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
      </select>
      <input type="number" min={2020} max={2030} value={periodo.ano}
        onChange={e => setPeriodo({ ...periodo, ano: Number(e.target.value) })}
        className="text-xs border-0 outline-none px-2 py-1 w-20 bg-transparent" />
    </div>
  )
}

// =================================================
// VISÃO EXECUTIVA — 3 cards estilo Mercado Turbo
// =================================================

function VisaoExecutiva({ periodo }: any) {
  const { data: comp } = useQuery<any>({
    queryKey: ['controladoria-comparativo'],
    queryFn: async () => (await api.get('/controladoria/dashboard-comparativo')).data,
    refetchInterval: 60000,
  })
  const { data: visao } = useQuery<any>({
    queryKey: ['controladoria-visao', periodo.ano, periodo.mes],
    queryFn: async () => (await api.get('/controladoria/visao-executiva',
      { params: { ano: periodo.ano, mes: periodo.mes } })).data,
  })

  if (!comp || !visao) return <Loading />

  const v = comp.vendas
  const k = visao.kpis

  return (
    <div className="space-y-4">
      {/* 3 cards comparativos estilo MT */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <CardComp titulo="Estatística Total de Vendas" cor="rose" linhas={[
          {
            label: 'Hoje / Ontem',
            atual: v.hoje.valor, ant: v.ontem.valor,
            qtdAtual: v.hoje.qtd, qtdAnt: v.ontem.qtd,
            var: v.var_hoje_pct,
          },
          {
            label: 'Esta Semana / Anterior',
            atual: v.semana.valor, ant: v.semana_ant.valor,
            qtdAtual: v.semana.qtd, qtdAnt: v.semana_ant.qtd,
            var: v.var_semana_pct,
          },
          {
            label: 'Este Mês / Mês Anterior',
            atual: v.mes.valor, ant: v.mes_ant.valor,
            qtdAtual: v.mes.qtd, qtdAnt: v.mes_ant.qtd,
            var: v.var_mes_pct,
          },
          {
            label: 'Vendas Canceladas',
            atual: v.canceladas_mes.valor, ant: v.canceladas_mes_ant.valor,
            qtdAtual: v.canceladas_mes.qtd, qtdAnt: v.canceladas_mes_ant.qtd,
            var: v.var_canc_pct, invertido: true,
          },
        ]} />

        <CardComp titulo="Estatísticas da Conta" cor="violet" mes={MESES_ABREV[periodo.mes - 1]} linhas={[
          {
            label: 'Atendimentos Abertos',
            valorTexto: String(comp.conta.perguntas_abertas),
          },
          {
            label: 'SLA Vencido (urgentes)',
            valorTexto: String(comp.conta.perguntas_sla_vencido),
            destaque: comp.conta.perguntas_sla_vencido > 0 ? 'red' : null,
          },
          {
            label: 'Pedidos no Mês',
            valorTexto: k.qtd_pedidos.toLocaleString('pt-BR'),
          },
          {
            label: 'Ticket Médio',
            valorTexto: formatMoney(k.ticket_medio),
          },
        ]} />

        <CardComp titulo="Envios" cor="emerald" linhas={[
          { label: 'Aprovados aguardando', valorTexto: String(comp.envios.aprovados_aguardando), botaoVer: '/vendas?status=aprovado' },
          { label: 'Em separação', valorTexto: String(comp.envios.em_separacao), botaoVer: '/vendas/ondas' },
          { label: 'Em embalagem', valorTexto: String(comp.envios.em_embalagem), botaoVer: '/vendas?status=embalagem' },
          { label: 'Aguardando NF', valorTexto: String(comp.envios.aguardando_nf), botaoVer: '/vendas?status=aguardando_nf' },
          { label: 'Faturados', valorTexto: String(comp.envios.faturados) },
          { label: 'Expedidos', valorTexto: String(comp.envios.expedidos) },
        ]} />
      </div>

      {/* KPIs do mês */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
        <Kpi label="Receita bruta" valor={k.receita} variacao={k.receita_var_pct} cor="emerald" />
        <Kpi label="Receita líquida" valor={k.receita_liquida} cor="emerald" />
        <Kpi label="CMV" valor={k.custo} cor="amber" />
        <Kpi label="Despesa Op." valor={k.despesa_op} cor="orange" />
        <Kpi label="Despesa Fin." valor={k.despesa_fin} cor="rose" sub="Comissão+frete" />
        <Kpi label="Resultado" valor={k.resultado} variacao={k.resultado_var_pct}
          cor={k.resultado >= 0 ? 'emerald' : 'red'}
          sub={`${k.resultado_pct.toFixed(1)}% sobre líquida`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card titulo="Receita por canal (DRE)" icone={<Layers size={14} />}>
          {visao.receita_por_canal_dre.length === 0 ? <Vazio msg="Sem receita por canal" /> : (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={visao.receita_por_canal_dre} dataKey="valor" nameKey="nome"
                  cx="50%" cy="50%" outerRadius={90}
                  label={(e: any) => `${((e.valor / k.receita) * 100).toFixed(0)}%`}>
                  {visao.receita_por_canal_dre.map((_: any, i: number) => (
                    <Cell key={i} fill={PALETA[i % PALETA.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v: number) => formatMoney(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card titulo="Vendas por canal (origem)" icone={<ShoppingCart size={14} />}>
          {visao.canais_vendas.length === 0 ? <Vazio msg="Sem vendas no período" /> : (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={visao.canais_vendas}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="canal" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => `R$${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v: number) => formatMoney(v)} />
                <Bar dataKey="valor" name="Faturamento">
                  {visao.canais_vendas.map((c: any, i: number) => (
                    <Cell key={i} fill={CORES_CANAL[c.canal] || PALETA[i % PALETA.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>
    </div>
  )
}

function CardComp({ titulo, cor, mes, linhas }: any) {
  const cores: any = {
    rose: 'bg-gradient-to-br from-rose-600 to-rose-700 text-white',
    violet: 'bg-gradient-to-br from-violet-600 to-violet-700 text-white',
    emerald: 'bg-gradient-to-br from-emerald-600 to-emerald-700 text-white',
  }
  return (
    <div className={`rounded-xl p-4 ${cores[cor] || cores.rose} shadow`}>
      <div className="text-center mb-3">
        <h3 className="text-sm font-bold uppercase tracking-wide">{titulo}</h3>
        {mes && <div className="text-[10px] opacity-80 mt-0.5">{mes}</div>}
      </div>
      <div className="space-y-2">
        {linhas.map((l: any, i: number) => (
          <div key={i} className="flex items-center justify-between gap-2 border-b border-white/20 pb-1.5 last:border-0">
            <span className="text-[11px] opacity-90 flex-1 truncate">{l.label}</span>
            <div className="flex items-center gap-2 text-right">
              {l.atual != null ? (
                <>
                  <span className="text-xs font-mono font-semibold">
                    {l.qtdAtual != null ? `${l.qtdAtual} / ${l.qtdAnt}` : ''}
                  </span>
                  {l.var != null && (
                    <span className={`text-[11px] font-mono font-bold ${
                      l.invertido
                        ? (l.var <= 0 ? 'text-emerald-200' : 'text-amber-200')
                        : (l.var >= 0 ? 'text-emerald-200' : 'text-amber-200')
                    }`}>
                      {l.var >= 0 ? '↑' : '↓'} {Math.abs(l.var).toFixed(1)}%
                    </span>
                  )}
                </>
              ) : (
                <span className={`text-base font-mono font-bold ${l.destaque === 'red' ? 'text-red-200' : ''}`}>
                  {l.valorTexto}
                </span>
              )}
              {l.botaoVer && (
                <a href={l.botaoVer}
                  className="text-[10px] px-1.5 py-0.5 rounded bg-white/20 hover:bg-white/30">
                  Ver
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// =================================================
// RESUMO FINANCEIRO — 5 KPIs + donut + breakdown
// =================================================

function ResumoFinanceiro({ periodo }: any) {
  const { data } = useQuery<any>({
    queryKey: ['controladoria-resumo', periodo.ano, periodo.mes],
    queryFn: async () => (await api.get('/controladoria/resumo-financeiro',
      { params: { ano: periodo.ano, mes: periodo.mes } })).data,
  })
  if (!data) return <Loading />
  const k = data.kpis_top

  return (
    <div className="space-y-4">
      {/* 5 KPI cards principais */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
        <KpiGrande label="Vendas Aprovadas" valor={k.vendas_aprovadas}
          subValor={`${k.qtd_aprovadas} pedidos · ${formatMoney(k.ticket_medio)}/un`}
          cor="slate" subRotulo={`Canceladas: ${k.qtd_canceladas} (${formatMoney(k.vendas_canceladas)})`} />
        <KpiGrande label="Custo & Imposto" valor={k.custo_imposto}
          subValor={`Custo: ${formatMoney(k.custo)} · Imposto: ${formatMoney(k.imposto)}`}
          cor="rose" />
        <KpiGrande label="Tarifa de Venda" valor={k.tarifa_venda}
          subValor={`Comissão: ${formatMoney(k.comissao)} · Adiant.: ${formatMoney(k.adiantamento)}`}
          cor="amber" />
        <KpiGrande label="Frete Total" valor={k.frete_total}
          subValor={`Devolução: ${formatMoney(k.devolucao)}`}
          cor="blue" />
        <KpiGrande label="Margem Contribuição" valor={k.margem_contribuicao}
          subValor={`${k.margem_pct.toFixed(2)}% · ${formatMoney(k.ticket_margem)}/un`}
          cor={k.margem_contribuicao >= 0 ? 'emerald' : 'red'} />
      </div>

      <Card titulo="Representação Gráfica" icone={<PieIcon size={14} />}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={data.donut.filter((d: any) => d.valor > 0)}
                dataKey="valor" nameKey="label"
                cx="50%" cy="50%" innerRadius={60} outerRadius={110} paddingAngle={2}
                label={(e: any) => k.vendas_aprovadas ? `${((e.valor / k.vendas_aprovadas) * 100).toFixed(1)}%` : ''}>
                {data.donut.map((d: any, i: number) => (
                  <Cell key={i} fill={d.cor || PALETA[i]} />
                ))}
              </Pie>
              <Tooltip formatter={(v: number) => formatMoney(v)} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1.5">
            {data.donut.map((d: any, i: number) => {
              const pct = k.vendas_aprovadas ? (d.valor / k.vendas_aprovadas) * 100 : 0
              return (
                <div key={i} className="flex items-center justify-between text-xs border-b border-ink-100 pb-1">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded" style={{ background: d.cor }}></div>
                    <span>{d.label}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-mono">{formatMoney(d.valor)}</span>
                    <span className="text-[10px] text-ink-500 w-12 text-right">{pct.toFixed(1)}%</span>
                  </div>
                </div>
              )
            })}
            <div className="flex items-center justify-between text-xs pt-2 mt-2 font-semibold">
              <span>Bruto vendas aprovadas</span>
              <span className="font-mono">{formatMoney(k.vendas_aprovadas)}</span>
            </div>
          </div>
        </div>
      </Card>
    </div>
  )
}

function KpiGrande({ label, valor, subValor, subRotulo, cor }: any) {
  const cores: any = {
    slate: 'bg-slate-50 border-slate-300 text-slate-900',
    rose: 'bg-rose-50 border-rose-300 text-rose-900',
    amber: 'bg-amber-50 border-amber-300 text-amber-900',
    blue: 'bg-blue-50 border-blue-300 text-blue-900',
    emerald: 'bg-emerald-50 border-emerald-300 text-emerald-900',
    red: 'bg-red-50 border-red-300 text-red-900',
  }
  return (
    <div className={`border-2 rounded-lg p-3 ${cores[cor] || cores.slate}`}>
      <div className="text-[10px] uppercase font-bold opacity-70 truncate">{label}</div>
      <div className="text-xl font-bold font-mono mt-1">{formatMoney(valor)}</div>
      {subValor && <div className="text-[10px] opacity-70 mt-1 truncate">{subValor}</div>}
      {subRotulo && <div className="text-[10px] opacity-60 mt-0.5 truncate">{subRotulo}</div>}
    </div>
  )
}

// =================================================
// DRE MENSAL — horizontal 12 meses
// =================================================

function DREMensal() {
  const [ano, setAno] = useState(() => new Date().getFullYear())
  const { data } = useQuery<any>({
    queryKey: ['controladoria-dre', ano],
    queryFn: async () => (await api.get('/controladoria/dre-mensal', { params: { ano } })).data,
  })

  if (!data) return <Loading />

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-ink-500">
          DRE em formato planilha — receita, custos e margem mês a mês de {ano}.
        </p>
        <input type="number" min={2020} max={2030} value={ano}
          onChange={e => setAno(Number(e.target.value))}
          className="text-xs border border-ink-300 rounded px-2 py-1 w-24 bg-white" />
      </div>

      <div className="bg-white border border-ink-200 rounded-lg overflow-x-auto">
        <table className="w-full text-xs min-w-[1200px]">
          <thead>
            <tr className="text-[10px] uppercase font-bold bg-ink-100">
              <th className="text-left p-2 sticky left-0 bg-ink-100 z-10 min-w-[200px]">Conta</th>
              {MESES_ABREV.map(m => (
                <th key={m} className="text-right p-2">{m}</th>
              ))}
              <th className="text-right p-2 bg-ink-200">Total</th>
            </tr>
          </thead>
          <tbody>
            {data.grupos.map((g: any) => (
              <DREGrupo key={g.tipo} grupo={g} />
            ))}
            {/* Totalizadores */}
            <DRELinhaTotal label="(=) RECEITA LÍQUIDA" cor="verde"
              meses={data.totalizadores.receita_liquida.meses}
              total={data.totalizadores.receita_liquida.total} />
            <DRELinhaTotal label="(=) MARGEM DE CONTRIBUIÇÃO" cor="verde"
              meses={data.totalizadores.margem_contribuicao.meses}
              total={data.totalizadores.margem_contribuicao.total} />
            <tr className="bg-emerald-100 text-[10px]">
              <td className="p-1.5 pl-4 italic sticky left-0 bg-emerald-100">% margem</td>
              {data.totalizadores.margem_contribuicao.pct_meses.map((p: number, i: number) => (
                <td key={i} className="text-right p-1.5 italic">{p.toFixed(1)}%</td>
              ))}
              <td className="text-right p-1.5 italic font-bold bg-emerald-200">
                {data.totalizadores.margem_contribuicao.pct_total.toFixed(1)}%
              </td>
            </tr>
            <DRELinhaTotal label="(=) LUCRO LÍQUIDO" cor="verde-escuro"
              meses={data.totalizadores.lucro_liquido.meses}
              total={data.totalizadores.lucro_liquido.total} />
            <tr className="bg-emerald-200 text-[10px]">
              <td className="p-1.5 pl-4 italic sticky left-0 bg-emerald-200">% lucro</td>
              {data.totalizadores.lucro_liquido.pct_meses.map((p: number, i: number) => (
                <td key={i} className="text-right p-1.5 italic">{p.toFixed(1)}%</td>
              ))}
              <td className="text-right p-1.5 italic font-bold bg-emerald-300">
                {data.totalizadores.lucro_liquido.pct_total.toFixed(1)}%
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}

function DREGrupo({ grupo }: any) {
  const [aberto, setAberto] = useState(true)
  const corFundo = grupo.cor === 'verde' ? 'bg-emerald-700 text-white' : 'bg-rose-700 text-white'
  return (
    <>
      <tr className={`${corFundo} cursor-pointer text-xs font-bold`} onClick={() => setAberto(!aberto)}>
        <td className="p-2 sticky left-0 z-10" style={{ background: 'inherit' }}>
          <span className="mr-1">{aberto ? '▾' : '▸'}</span>{grupo.label}
        </td>
        {grupo.totais_mes.map((v: number, i: number) => (
          <td key={i} className="text-right p-2 font-mono">{v ? formatMoney(v) : '-'}</td>
        ))}
        <td className="text-right p-2 font-mono font-bold" style={{ background: 'rgba(0,0,0,0.15)' }}>
          {grupo.total ? formatMoney(grupo.total) : '-'}
        </td>
      </tr>
      {aberto && grupo.items.map((item: any) => (
        <tr key={item.codigo} className="border-b border-ink-100 hover:bg-ink-50/50">
          <td className="p-1.5 pl-6 sticky left-0 bg-white">
            <span className="font-mono text-[10px] text-ink-400 mr-1">{item.codigo}</span>
            {item.nome}
          </td>
          {item.meses.map((v: number, i: number) => (
            <td key={i} className="text-right p-1.5 font-mono text-[11px]">{v ? formatMoney(v) : '-'}</td>
          ))}
          <td className="text-right p-1.5 font-mono text-[11px] font-semibold bg-ink-50">
            {item.total ? formatMoney(item.total) : '-'}
          </td>
        </tr>
      ))}
    </>
  )
}

function DRELinhaTotal({ label, cor, meses, total }: any) {
  const corBg = cor === 'verde' ? 'bg-emerald-600 text-white' : cor === 'verde-escuro' ? 'bg-emerald-800 text-white' : 'bg-rose-600 text-white'
  return (
    <tr className={`${corBg} text-xs font-bold`}>
      <td className="p-2 sticky left-0 z-10" style={{ background: 'inherit' }}>{label}</td>
      {meses.map((v: number, i: number) => (
        <td key={i} className="text-right p-2 font-mono">{v ? formatMoney(v) : '-'}</td>
      ))}
      <td className="text-right p-2 font-mono font-bold" style={{ background: 'rgba(0,0,0,0.2)' }}>
        {total ? formatMoney(total) : '-'}
      </td>
    </tr>
  )
}

// =================================================
// VENDAS DETALHADA — decomposição de margem inline
// =================================================

function VendasDetalhada({ periodo }: any) {
  const [canal, setCanal] = useState('')
  const { data } = useQuery<any[]>({
    queryKey: ['controladoria-vendas-det', periodo.ano, periodo.mes, canal],
    queryFn: async () => (await api.get('/controladoria/vendas-detalhadas',
      { params: { ano: periodo.ano, mes: periodo.mes, canal: canal || undefined, limit: 200 } })).data,
  })

  if (!data) return <Loading />

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <p className="text-xs text-ink-500">
          Decomposição: Bruto − Comissão − Adiant. − Frete − Custo − Imposto = Margem.
          <span className="ml-1 text-amber-700">Valor com <em>(est)</em> = não há taxa real cruzada (usa % padrão do canal).</span>
        </p>
        <select value={canal} onChange={e => setCanal(e.target.value)}
          className="input text-xs h-8 w-44">
          <option value="">Todos os canais</option>
          <option value="mercado_livre">Mercado Livre</option>
          <option value="tray">Tray</option>
          <option value="balcao">PDV</option>
          <option value="digitado">Digitado</option>
        </select>
      </div>

      <div className="bg-white border border-ink-200 rounded-lg overflow-x-auto">
        <table className="w-full text-xs min-w-[1200px]">
          <thead>
            <tr className="text-[10px] uppercase font-bold bg-ink-100 text-ink-600">
              <th className="text-left p-2">Pedido</th>
              <th className="text-left p-2">Canal</th>
              <th className="text-left p-2">Cliente</th>
              <th className="text-right p-2">Bruto</th>
              <th className="text-right p-2">Comissão</th>
              <th className="text-right p-2">Adiant.</th>
              <th className="text-right p-2">Frete</th>
              <th className="text-right p-2">Imposto</th>
              <th className="text-right p-2">Custo</th>
              <th className="text-right p-2">Margem</th>
              <th className="text-right p-2">MC %</th>
              <th className="text-left p-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr><td colSpan={12} className="p-8 text-center text-ink-400">Sem vendas no período</td></tr>
            ) : data.map((p: any) => (
              <tr key={p.id} className="border-t border-ink-100 hover:bg-blue-50/30">
                <td className="p-2 font-mono">#{p.numero}</td>
                <td className="p-2">
                  <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold"
                    style={{ background: CORES_CANAL[p.canal] || '#e5e7eb', color: '#fff' }}>
                    {p.canal}
                  </span>
                </td>
                <td className="p-2 truncate max-w-[180px]">{p.cliente}</td>
                <td className="text-right p-2 font-mono font-semibold text-ink-900">{formatMoney(p.bruto)}</td>
                <td className={`text-right p-2 font-mono ${p.comissao_estimada ? 'text-amber-700 italic' : 'text-rose-700'}`}
                    title={p.comissao_estimada ? 'Estimado: cross-lookup com MP id pendente' : 'Real do MP API'}>
                  −{formatMoney(p.comissao)}{p.comissao_estimada ? ' (est)' : ''}
                </td>
                <td className="text-right p-2 font-mono text-orange-700">{p.taxa_adiantamento ? `−${formatMoney(p.taxa_adiantamento)}` : '-'}</td>
                <td className="text-right p-2 font-mono text-blue-700">{p.taxa_frete ? `−${formatMoney(p.taxa_frete)}` : '-'}</td>
                <td className="text-right p-2 font-mono text-purple-700 italic" title="Estimado 6% (Simples Nacional padrão)">
                  −{formatMoney(p.imposto)}
                </td>
                <td className="text-right p-2 font-mono text-amber-700">−{formatMoney(p.custo)}</td>
                <td className={`text-right p-2 font-mono font-bold ${p.margem >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                  {formatMoney(p.margem)}
                </td>
                <td className={`text-right p-2 font-mono font-bold ${p.margem_pct >= 30 ? 'text-emerald-700' : p.margem_pct >= 10 ? 'text-amber-700' : 'text-red-700'}`}>
                  {p.margem_pct.toFixed(1)}%
                </td>
                <td className="p-2 text-[10px] text-ink-500">{p.status}</td>
              </tr>
            ))}
          </tbody>
          {data.length > 0 && (
            <tfoot className="bg-ink-100">
              <tr className="font-bold text-xs">
                <td colSpan={3} className="p-2">Totais ({data.length} pedidos)</td>
                <td className="text-right p-2 font-mono">{formatMoney(data.reduce((a, p) => a + p.bruto, 0))}</td>
                <td className="text-right p-2 font-mono text-rose-700">−{formatMoney(data.reduce((a, p) => a + p.comissao, 0))}</td>
                <td className="text-right p-2 font-mono text-orange-700">−{formatMoney(data.reduce((a, p) => a + p.taxa_adiantamento, 0))}</td>
                <td className="text-right p-2 font-mono text-blue-700">−{formatMoney(data.reduce((a, p) => a + p.taxa_frete, 0))}</td>
                <td className="text-right p-2 font-mono text-purple-700">−{formatMoney(data.reduce((a, p) => a + p.imposto, 0))}</td>
                <td className="text-right p-2 font-mono text-amber-700">−{formatMoney(data.reduce((a, p) => a + p.custo, 0))}</td>
                <td className="text-right p-2 font-mono text-emerald-700">{formatMoney(data.reduce((a, p) => a + p.margem, 0))}</td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  )
}

// =================================================
// EVOLUÇÃO MENSAL
// =================================================

function Evolucao() {
  const [meses, setMeses] = useState(12)
  const { data } = useQuery<any>({
    queryKey: ['controladoria-evolucao', meses],
    queryFn: async () => (await api.get('/controladoria/evolucao-mensal', { params: { meses } })).data,
  })

  if (!data) return <Loading />

  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-2">
        {[6, 12, 24].map(m => (
          <button key={m} onClick={() => setMeses(m)}
            className={`text-xs px-2 py-1 rounded ${meses === m ? 'bg-violet-600 text-white' : 'bg-ink-100 hover:bg-ink-200'}`}>
            {m} meses
          </button>
        ))}
      </div>

      <Card titulo="Receita líquida × resultado" icone={<TrendingUp size={14} />}>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={data.serie}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => `R$${(v / 1000).toFixed(0)}k`} />
            <Tooltip formatter={(v: number) => formatMoney(v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Area type="monotone" dataKey="receita_liquida" stroke="#10b981" fill="#10b981" fillOpacity={0.2} name="Receita líq." />
            <Area type="monotone" dataKey="resultado" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.3} name="Resultado" />
          </AreaChart>
        </ResponsiveContainer>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card titulo="Despesas mensais (stacked)" icone={<TrendingDown size={14} />}>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={data.serie}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="mes" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v: number) => `R$${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v: number) => formatMoney(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="custo" stackId="a" fill="#f59e0b" name="CMV" />
              <Bar dataKey="despesa_op" stackId="a" fill="#fb923c" name="Op." />
              <Bar dataKey="despesa_fin" stackId="a" fill="#ef4444" name="Fin." />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card titulo="Margem % mensal" icone={<Activity size={14} />}>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={data.serie}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="mes" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v: number) => `${v.toFixed(0)}%`} />
              <Tooltip formatter={(v: number) => `${v.toFixed(1)}%`} />
              <Line type="monotone" dataKey="margem_pct" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 3 }} name="Margem %" />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  )
}

// =================================================
// MARGEM DE PRODUTOS
// =================================================

function MargemProdutos({ periodo }: any) {
  const { data } = useQuery<any>({
    queryKey: ['controladoria-produtos', periodo.ano, periodo.mes],
    queryFn: async () => (await api.get('/controladoria/margem-produtos',
      { params: { ano: periodo.ano, mes: periodo.mes, limit: 50 } })).data,
  })
  if (!data) return <Loading />
  const top10 = (data.produtos || []).slice(0, 10)

  return (
    <div className="space-y-4">
      <Card titulo="Top 10 produtos por receita" icone={<Package size={14} />}>
        {top10.length === 0 ? <Vazio msg="Sem vendas" /> : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={top10} layout="vertical" margin={{ left: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v: number) => `R$${(v / 1000).toFixed(0)}k`} />
              <YAxis type="category" dataKey="codigo" tick={{ fontSize: 9 }} width={70} />
              <Tooltip formatter={(v: number) => formatMoney(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="receita" fill="#10b981" name="Receita" />
              <Bar dataKey="custo" fill="#f59e0b" name="Custo" />
              <Bar dataKey="margem" fill="#8b5cf6" name="Margem" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>

      <Card titulo="Detalhamento (top 50)" icone={<Layers size={14} />}>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-ink-50">
              <tr className="text-[10px] uppercase font-semibold text-ink-600">
                <th className="text-left p-2">SKU</th>
                <th className="text-left p-2">Produto</th>
                <th className="text-right p-2">Qtd</th>
                <th className="text-right p-2">Preço médio</th>
                <th className="text-right p-2">Receita</th>
                <th className="text-right p-2">Custo</th>
                <th className="text-right p-2">Margem</th>
                <th className="text-right p-2">MC %</th>
              </tr>
            </thead>
            <tbody>
              {(data.produtos || []).map((p: any) => (
                <tr key={p.id} className="border-t border-ink-100 hover:bg-blue-50/30">
                  <td className="p-2 font-mono text-[10px]">{p.codigo}</td>
                  <td className="p-2 truncate max-w-xs">{p.descricao}</td>
                  <td className="text-right p-2 font-mono">{p.qtd.toFixed(0)}</td>
                  <td className="text-right p-2 font-mono">{formatMoney(p.preco_medio)}</td>
                  <td className="text-right p-2 font-mono text-emerald-700 font-semibold">{formatMoney(p.receita)}</td>
                  <td className="text-right p-2 font-mono text-amber-700">{formatMoney(p.custo)}</td>
                  <td className="text-right p-2 font-mono font-bold">{formatMoney(p.margem)}</td>
                  <td className={`text-right p-2 font-mono ${p.margem_pct >= 30 ? 'text-emerald-700' : p.margem_pct >= 15 ? 'text-amber-700' : 'text-red-700'}`}>
                    {p.margem_pct.toFixed(1)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}

// =================================================
// MARKETPLACE
// =================================================

function Marketplace() {
  // Resumo Financeiro ML completo (estilo Mercado Turbo) — KPIs, modalidades,
  // tabela detalhada, ranking, dias perdidos, export.
  return <ResumoFinanceiroMLPage />
}

// =================================================
// DESPESAS
// =================================================

function Despesas({ periodo }: any) {
  const { data } = useQuery<any>({
    queryKey: ['controladoria-despesas', periodo.ano, periodo.mes],
    queryFn: async () => (await api.get('/controladoria/despesas',
      { params: { ano: periodo.ano, mes: periodo.mes } })).data,
  })

  if (!data) return <Loading />

  const por_tipo: Record<string, any[]> = {}
  for (const d of data.breakdown) {
    por_tipo[d.tipo] = por_tipo[d.tipo] || []
    por_tipo[d.tipo].push(d)
  }
  const tipoLabels: Record<string, string> = {
    despesa_op: 'Despesas Operacionais',
    despesa_fin: 'Despesas Financeiras',
    custo: 'Custos (CMV)',
    deducao: 'Deduções (devoluções, descontos)',
  }
  const total = data.breakdown.reduce((a: number, d: any) => a + d.valor, 0)

  return (
    <div className="space-y-4">
      <Card titulo="Pizza geral de despesas" icone={<PieIcon size={14} />}>
        {data.breakdown.length === 0 ? <Vazio msg="Sem despesas" /> : (
          <ResponsiveContainer width="100%" height={350}>
            <PieChart>
              <Pie data={data.breakdown} dataKey="valor" nameKey="nome"
                cx="50%" cy="50%" outerRadius={120}
                label={(e: any) => total ? `${((e.valor / total) * 100).toFixed(0)}%` : ''}>
                {data.breakdown.map((_: any, i: number) => (
                  <Cell key={i} fill={PALETA[i % PALETA.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(v: number) => formatMoney(v)} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
            </PieChart>
          </ResponsiveContainer>
        )}
      </Card>

      {Object.entries(por_tipo).map(([tipo, items]) => (
        <Card key={tipo} titulo={tipoLabels[tipo] || tipo}>
          <table className="w-full text-xs">
            <thead className="bg-ink-50">
              <tr className="text-[10px] uppercase font-semibold text-ink-600">
                <th className="text-left p-2">Conta</th>
                <th className="text-left p-2">Nome</th>
                <th className="text-right p-2">Lançamentos</th>
                <th className="text-right p-2">Valor</th>
                <th className="text-right p-2">% do total</th>
              </tr>
            </thead>
            <tbody>
              {items.map((d: any) => (
                <tr key={d.codigo} className="border-t border-ink-100">
                  <td className="p-2 font-mono text-[11px]">{d.codigo}</td>
                  <td className="p-2">{d.nome}</td>
                  <td className="text-right p-2 font-mono">{d.qtd}</td>
                  <td className="text-right p-2 font-mono font-semibold text-rose-700">{formatMoney(d.valor)}</td>
                  <td className="text-right p-2 font-mono">{total ? ((d.valor / total) * 100).toFixed(1) : 0}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ))}

      <Card titulo="Top 15 fornecedores (contas pagas)" icone={<Users size={14} />}>
        {data.top_fornecedores.length === 0 ? <Vazio msg="Sem pagamentos" /> : (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={data.top_fornecedores} layout="vertical" margin={{ left: 100 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v: number) => `R$${(v / 1000).toFixed(0)}k`} />
              <YAxis type="category" dataKey="nome" tick={{ fontSize: 9 }} width={120} />
              <Tooltip formatter={(v: number) => formatMoney(v)} />
              <Bar dataKey="valor" fill="#8b5cf6" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>
    </div>
  )
}

// =================================================
// FLUXO & AGING
// =================================================

function Fluxo() {
  const { data: aging } = useQuery<any>({
    queryKey: ['controladoria-aging'],
    queryFn: async () => (await api.get('/controladoria/aging-receber')).data,
  })
  const { data: formas } = useQuery<any[]>({
    queryKey: ['controladoria-formas'],
    queryFn: async () => (await api.get('/controladoria/forma-pagamento', { params: { meses: 3 } })).data,
  })
  const { data: clientes } = useQuery<any[]>({
    queryKey: ['controladoria-clientes'],
    queryFn: async () => (await api.get('/controladoria/top-clientes', { params: { meses: 3, limit: 15 } })).data,
  })
  const { data: estoque } = useQuery<any>({
    queryKey: ['controladoria-estoque'],
    queryFn: async () => (await api.get('/controladoria/estoque-saude')).data,
  })

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card titulo="Aging de contas a receber" icone={<AlertCircle size={14} />}>
          {!aging ? <Loading /> : (
            <>
              <div className="text-xs text-ink-600 mb-2">
                Total aberto: <strong className="text-ink-900">{formatMoney(aging.total_aberto)}</strong> ·
                Vencido: <strong className="text-rose-700">{formatMoney(aging.total_vencido)}</strong>
                ({aging.pct_vencido.toFixed(1)}%)
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={aging.buckets}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={(v: number) => `R$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v: number) => formatMoney(v)} />
                  <Bar dataKey="saldo" name="Saldo">
                    {aging.buckets.map((b: any, i: number) => (
                      <Cell key={i} fill={
                        b.bucket === 'a_vencer' ? '#10b981'
                          : b.bucket === 'venc_1_30' ? '#f59e0b'
                          : b.bucket === 'venc_31_60' ? '#fb923c'
                          : '#ef4444'
                      } />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </>
          )}
        </Card>

        <Card titulo="Forma de pagamento × ticket médio" icone={<DollarSign size={14} />}>
          {!formas ? <Loading /> : formas.length === 0 ? <Vazio msg="Sem dados" /> : (
            <table className="w-full text-xs">
              <thead className="bg-ink-50">
                <tr className="text-[10px] uppercase font-semibold text-ink-600">
                  <th className="text-left p-2">Forma</th>
                  <th className="text-right p-2">Pedidos</th>
                  <th className="text-right p-2">Bruto</th>
                  <th className="text-right p-2">Ticket</th>
                </tr>
              </thead>
              <tbody>
                {formas.map((f: any) => (
                  <tr key={f.forma} className="border-t border-ink-100">
                    <td className="p-2">{f.forma}</td>
                    <td className="text-right p-2 font-mono">{f.qtd}</td>
                    <td className="text-right p-2 font-mono">{formatMoney(f.bruto)}</td>
                    <td className="text-right p-2 font-mono font-semibold">{formatMoney(f.ticket)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>

      <Card titulo="Top clientes (últimos 3 meses)" icone={<Users size={14} />}>
        {!clientes ? <Loading /> : clientes.length === 0 ? <Vazio msg="Sem clientes" /> : (
          <table className="w-full text-xs">
            <thead className="bg-ink-50">
              <tr className="text-[10px] uppercase font-semibold text-ink-600">
                <th className="text-left p-2">Cliente</th>
                <th className="text-left p-2">CPF/CNPJ</th>
                <th className="text-right p-2">Pedidos</th>
                <th className="text-right p-2">Total</th>
                <th className="text-right p-2">Ticket</th>
                <th className="text-right p-2">Última compra</th>
              </tr>
            </thead>
            <tbody>
              {clientes.map((c: any) => (
                <tr key={c.id} className="border-t border-ink-100 hover:bg-blue-50/30">
                  <td className="p-2 truncate max-w-xs">{c.nome}</td>
                  <td className="p-2 font-mono text-[10px]">{c.documento || '—'}</td>
                  <td className="text-right p-2 font-mono">{c.pedidos}</td>
                  <td className="text-right p-2 font-mono font-semibold text-emerald-700">{formatMoney(c.bruto)}</td>
                  <td className="text-right p-2 font-mono">{formatMoney(c.ticket)}</td>
                  <td className="text-right p-2 text-[10px] text-ink-500">
                    {c.ultima_compra ? new Date(c.ultima_compra).toLocaleDateString('pt-BR') : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card titulo="Saúde do estoque" icone={<Package size={14} />}>
        {!estoque ? <Loading /> : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <BlocoEst label="Produtos ativos" valor={estoque.ativos.toLocaleString('pt-BR')} cor="blue" />
            <BlocoEst label="Em ruptura" valor={estoque.ruptura.toLocaleString('pt-BR')} cor="red" />
            <BlocoEst label="Sem custo" valor={estoque.sem_custo.toLocaleString('pt-BR')} cor="amber" />
            <BlocoEst label="Margem potencial" valor={`${estoque.margem_potencial_pct.toFixed(1)}%`} cor="emerald" />
            <BlocoEst label="Capital empatado (custo)" valor={formatMoney(estoque.valor_custo)} cor="violet" full />
            <BlocoEst label="Valor de venda potencial" valor={formatMoney(estoque.valor_venda)} cor="emerald" full />
          </div>
        )}
      </Card>
    </div>
  )
}

// =================================================
// HELPERS
// =================================================

function Card({ titulo, icone, children }: any) {
  return (
    <div className="bg-white border border-ink-200 rounded-lg p-3">
      <h3 className="text-xs font-semibold text-ink-700 uppercase mb-2 flex items-center gap-1.5">
        {icone} {titulo}
      </h3>
      {children}
    </div>
  )
}

function Kpi({ label, valor, sub, cor = 'blue', variacao }: any) {
  const cores: any = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    blue: 'bg-blue-50 border-blue-200 text-blue-900',
    violet: 'bg-violet-50 border-violet-200 text-violet-900',
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    orange: 'bg-orange-50 border-orange-200 text-orange-900',
    rose: 'bg-rose-50 border-rose-200 text-rose-900',
    red: 'bg-red-50 border-red-200 text-red-900',
  }
  const variacaoClasse = variacao == null ? '' : variacao >= 0 ? 'text-emerald-700' : 'text-red-700'
  return (
    <div className={`border rounded-lg p-3 ${cores[cor] || cores.blue}`}>
      <div className="text-[10px] uppercase font-semibold opacity-70 truncate">{label}</div>
      <div className="text-lg font-bold font-mono mt-0.5">
        {typeof valor === 'number' ? formatMoney(valor) : valor}
      </div>
      {sub && <div className="text-[10px] opacity-60 mt-0.5 truncate">{sub}</div>}
      {variacao != null && (
        <div className={`text-[10px] font-mono mt-0.5 ${variacaoClasse}`}>
          {variacao >= 0 ? '↑' : '↓'} {Math.abs(variacao).toFixed(1)}% vs mês ant.
        </div>
      )}
    </div>
  )
}

function BlocoEst({ label, valor, cor = 'blue', full = false }: any) {
  const cores: any = {
    blue: 'bg-blue-50 border-blue-200 text-blue-900',
    red: 'bg-red-50 border-red-200 text-red-900',
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    violet: 'bg-violet-50 border-violet-200 text-violet-900',
  }
  return (
    <div className={`border rounded p-3 ${cores[cor]} ${full ? 'col-span-2' : ''}`}>
      <div className="text-[10px] uppercase font-semibold opacity-70">{label}</div>
      <div className="text-lg font-bold font-mono mt-0.5">{valor}</div>
    </div>
  )
}

function Loading() {
  return <div className="p-12 text-center text-ink-400 text-sm">Carregando...</div>
}

function Vazio({ msg }: { msg: string }) {
  return <div className="p-8 text-center text-ink-400 text-xs">{msg}</div>
}
